/* eslint-disable no-console */
import Message from 'view/shared/message';
import { getHistory } from 'modules/history';
import AuthService from 'modules/auth/authService';
import { SignInResponseStatus } from 'modules/auth/constants';

import { i18n } from 'i18n';

const DEFAULT_ERROR_MESSAGE = i18n('errors.defaultErrorMessage');

function selectErrorMessage(error) {
  if (error) {
    if (error.data) {
      // standard lean api error format => data.error
      // sanbox.leantech.me api error format => data.message
      return error.data.error || error.data.message;
    }
  }

  return error?.response?.statusText || DEFAULT_ERROR_MESSAGE;
}

function selectErrorCode(error) {
  if (error && error.response && error.response.status) {
    return error.response.status;
  }

  if (error && error.data && error.data.status) {
    return error.data.status;
  }

  return 500;
}

// The backend ladder tops out at 30 minutes, so an hour is well beyond anything legitimate.
const MAX_RETRY_AFTER_SECONDS = 3600;

// apiFetch rejects with the raw fetch Response, whose `headers` is a Headers instance and cannot be
// read by key. Plain objects are still accepted, matched case-insensitively.
function selectHeader(error, name) {
  const headers = error?.response?.headers;

  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    return headers.get(name);
  }

  const target = name.toLowerCase();
  const key = Object.keys(headers).find(
    candidate => candidate.toLowerCase() === target,
  );

  return key === undefined ? undefined : headers[key];
}

// CMS always sends whole seconds, never the RFC http-date form, so anything else is unreadable.
function selectRetryAfterSeconds(error) {
  const raw = selectHeader(error, 'retry-after');

  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }

  const seconds = Number(raw);

  if (!Number.isInteger(seconds) || seconds <= 0) {
    return undefined;
  }

  return Math.min(seconds, MAX_RETRY_AFTER_SECONDS);
}

function selectRetryAfterMinutes(error) {
  const seconds = selectRetryAfterSeconds(error);

  if (seconds === undefined || seconds < 60) {
    return undefined;
  }

  return Math.ceil(seconds / 60);
}

function selectErrorStatusOrStatusCode(error) {
  if (error && error.data) {
    return error.data.status || error.data.status_code;
  }

  return '';
}

export default class Errors {
  static handle(error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(selectErrorMessage(error));
      console.error(error);
    }

    if (selectErrorCode(error) === 403) {
      getHistory().push('/403');
      return;
    }

    if (selectErrorCode(error) === 400) {
      Message.error(selectErrorMessage(error));
      return;
    }

    if (selectErrorCode(error) === 409) {
      Message.error(selectErrorMessage(error));
      return;
    }

    // If not authorized redirect to signin URL
    if (selectErrorCode(error) === 401) {
      AuthService.signout()
      getHistory().push('/auth/signin');
      Message.error(selectErrorMessage(error));
      return;
    }

    if (selectErrorCode(error) === 429) {
      getHistory().push('/429');
      return;
    }

    if (selectErrorCode(error) === 404) {
      getHistory().push('/404');
      return;
    }

    if (selectErrorCode(error) === 500 && selectErrorStatusOrStatusCode(error) === 'EXTERNAL_CLIENT_SERVICE_FAILURE') {
      Message.error(selectErrorMessage(error));
      return;
    }

    getHistory().push('/500');
  }

  static errorCode(error) {
    return selectErrorCode(error);
  }

  // CMS applies a progressive per-email backoff to repeated sign-in failures. Matching on the body
  // status as well as the code keeps a bare 429 from the edge gateway on its existing `/429` page.
  static isLoginThrottled(error) {
    return (
      selectErrorCode(error) === 429 &&
      selectErrorStatusOrStatusCode(error) ===
        SignInResponseStatus.tooManyAttempts
    );
  }

  // Exact seconds to wait, or null when Retry-After is unreadable.
  static retryAfterSeconds(error) {
    return selectRetryAfterSeconds(error) ?? null;
  }

  // Sub-minute waits stay vague rather than rounding the backend's 30s step up to "1 minute".
  // Used by the in-app MFA flows only.
  static loginThrottleMessage(error) {
    const minutes = selectRetryAfterMinutes(error);

    if (minutes === undefined) {
      return i18n('auth.tooManyAttempts');
    }

    return minutes === 1
      ? i18n('auth.tooManyAttemptsOneMinute')
      : i18n('auth.tooManyAttemptsMinutes', minutes);
  }

  static errorStatusOrStatusCode(error) {
    return selectErrorStatusOrStatusCode(error);
  }

  static selectMessage(error) {
    return selectErrorMessage(error);
  }

  static showMessage(error) {
    Message.error(selectErrorMessage(error));
  }
}
